import { Button } from "../../../../core";
import { useTranslation } from "react-i18next";
import { CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Text } from "@core/text";

interface AssignmentSuccessModalProps {
  onClose: () => void;
  onGoBack: () => void;
}

const AssignmentSuccessModal = ({
  onClose,
  onGoBack,
}: AssignmentSuccessModalProps) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70">
        {/* Confetti effect */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ pointerEvents: "none" }}
        >
          {Array.from({ length: 30 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              initial={{
                x: Math.random() * window.innerWidth,
                y: -20,
                scale: Math.random() * 0.5 + 0.5,
                opacity: 1,
              }}
              animate={{
                y: window.innerHeight + 20,
                rotate: Math.random() * 360,
                opacity: 0,
              }}
              transition={{
                duration: Math.random() * 2 + 1,
                ease: "easeOut",
                delay: Math.random() * 0.5,
              }}
              style={{
                width: "10px",
                height: "10px",
                borderRadius: Math.random() > 0.5 ? "50%" : "0",
                background: [
                  "#4CAF50", // Green
                  "#8BC34A", // Light Green
                  "#CDDC39", // Lime
                  "#2196F3", // Blue
                  "#03A9F4", // Light Blue
                  "#00BCD4", // Cyan
                ][Math.floor(Math.random() * 6)],
              }}
            />
          ))}
        </div>

        <motion.div
          className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", damping: 12 }}
        >
          <div className="p-6">
            <div className="flex flex-col items-center text-center mb-6">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 8 }}
                className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4"
              >
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Text variant={"h2"} className=" mb-2">
                  notifications.assignment.assignSuccess
                </Text>
              </motion.div>
            </div>

            <div className="flex justify-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="ghost"
                  size="md"
                  rounded="lg"
                  bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                  textColor="text-gray-800 dark:text-gray-100"
                  onClick={onGoBack}
                >
                  {t("maintenance.returnToHomepage")}
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="solid"
                  size="md"
                  rounded="lg"
                  bgColor="bg-primary-500 hover:bg-primary-600"
                  textColor="text-white"
                  onClick={onClose}
                >
                  {t("assignments.viewAssignedAssignment")}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AssignmentSuccessModal;
